// The rebuilt employment-history screen, behind a company toggle.
//
// It reads and writes the same form the old accordion does, so nothing about
// how the application is submitted changes here — the difference is entirely in
// how a driver reaches those values. Per-entry persistence arrives separately;
// today a Save writes into the form and the existing Continue still submits it.

import { useEffect, useMemo, useRef } from 'react'

import { v1 as uuid } from 'uuid'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { currentMonthIndex } from 'src/utils/date/monthPeriods'
import type { MonthPeriod } from 'src/utils/date/monthPeriods'

import { EmploymentHistoryList } from './components/EmploymentHistoryList'
import type { ListEntry } from './components/EmploymentHistoryList'
import { EntryModal } from './components/EntryModal'
import { attentionFor } from './lib/attention'
import { COPYABLE_CERTIFICATIONS } from './lib/certifications'
import { analyzeCoverage } from './lib/coverage'
import { toEntryDraft } from './lib/formBridge'
import type { FormExperience } from './lib/formBridge'
import { describeSchemaProblem, schemaProblems } from './lib/schemaProblems'
import { useCarrierContacts } from './lib/useCarrierContacts'
import { useCarrierSearch } from './lib/useCarrierSearch'
import { useEmploymentPersistence } from './lib/useEmploymentPersistence'
import { useEmploymentTelemetry } from './lib/useEmploymentTelemetry'
import { useEntryFlow } from './lib/useEntryFlow'
import type { SavedEntry } from './lib/useEntryFlow'

export interface EmploymentHistoryScreenProps {
  experiences: FormExperience[]
  onExperiencesChange: (experiences: FormExperience[]) => void
  requiredYears: number | null
  onContinue: () => void
  onBack?: () => void
  backText?: string
  /** Sends the whole application. Every entry goes up on every save. */
  submit: (payload: unknown) => Promise<unknown>
  busy?: boolean
  now?: Date
}

export const EmploymentHistoryScreen = ({
  experiences,
  onExperiencesChange,
  requiredYears,
  onContinue,
  onBack,
  backText,
  submit,
  busy = false,
  now = new Date(),
}: EmploymentHistoryScreenProps) => {
  const nowMonth = currentMonthIndex(now)

  const { saveEntry, removeEntry, saving, error } = useEmploymentPersistence({
    experiences,
    onExperiencesChange,
    submit,
    newId: () => uuid(),
  })

  // A refused save has to say so. Left silent it looks like the entry was kept,
  // and the driver finds out when the page reloads without it.
  const reportedError = useRef<unknown>(null)
  useEffect(() => {
    if (error && error !== reportedError.current) {
      reportedError.current = error
      toast(
        error instanceof Error ? error.message : 'Unable to save. Try again.',
        'error'
      )
    }
  }, [error])

  const telemetry = useEmploymentTelemetry()

  // Read at Save, which runs before this render's lookup is declared
  const platformOnlyRef = useRef(false)

  const [flow, dispatch] = useEntryFlow(({ draft, id }: SavedEntry) => {
    telemetry.entrySaved({
      kind: draft.kind,
      isNew: id == null,
      carrierSelected: Boolean(draft.carrierSelected),
      hasContact: Boolean(draft.phone || draft.email),
      contactNotNeeded: draft.kind === 'employment' && platformOnlyRef.current,
      nonDrivingJob:
        draft.kind === 'employment' && Boolean(draft.nonDrivingJob),
    })
    void saveEntry(draft, id)
  }, nowMonth)

  // Only searches while the sheet is asking for an employer; leaving the search
  // step stops the query rather than leaving it running behind the form.
  const search = useCarrierSearch(flow.mode === 'search' ? flow.query : '')

  // Contacts we already hold for the chosen carrier. Only a registry carrier has
  // a DOT number to look them up by.
  const {
    contacts: knownContacts,
    loading: contactsLoading,
    platformOnly,
  } = useCarrierContacts(flow.draft.dotNumber)
  platformOnlyRef.current = platformOnly

  // An employer that answers verifications only through a platform does not
  // need the driver to find anyone, so they are never asked to type a contact:
  // with none held, the step is passed without asking. With some held, the step
  // still shows with one pre-selected — a tap to accept, and the driver sees
  // what goes on their application. Not when the driver opened it from the
  // form either: that is a choice to add one.
  useEffect(() => {
    if (flow.mode !== 'contact' || flow.contactByRequest) return
    if (contactsLoading || !platformOnly || knownContacts.length > 0) return
    telemetry.contactNotNeeded({ dotNumber: flow.draft.dotNumber })
    dispatch({ type: 'contactNotNeeded' })
  }, [
    contactsLoading,
    dispatch,
    flow.contactByRequest,
    flow.draft.dotNumber,
    flow.mode,
    knownContacts.length,
    platformOnly,
    telemetry,
  ])

  // Pre-select the first, once, when the contact step opens with none chosen.
  // A default the driver can move is the whole intervention: it is what turns
  // "no contact recorded" into "the contact we already had".
  const preselectedFor = useRef<string | null>(null)
  useEffect(() => {
    if (flow.mode !== 'contact') return
    if (flow.contact || knownContacts.length === 0) return
    const key = `${flow.editingId ?? 'new'}:${flow.draft.dotNumber ?? ''}`
    if (preselectedFor.current === key) return
    preselectedFor.current = key
    dispatch({
      type: 'chooseContact',
      contact: { ...knownContacts[0], source: 'known_default' },
    })
  }, [
    dispatch,
    flow.contact,
    flow.draft.dotNumber,
    flow.editingId,
    flow.mode,
    knownContacts,
  ])

  // Report a settled search once per result set, not once per render
  const reported = useRef<string>('')
  useEffect(() => {
    if (!search.searched || search.searching) return
    const key = `${flow.query}:${search.results.length}`
    if (reported.current === key) return
    reported.current = key
    telemetry.searchResults({
      query: flow.query,
      resultsCount: search.results.length,
      normalizedQuery: search.normalizedQuery,
      dotLookup: search.dotLookup,
    })
  }, [
    flow.query,
    search.searched,
    search.searching,
    search.results.length,
    search.normalizedQuery,
    search.dotLookup,
    telemetry,
  ])

  // Where the driver stood when they left the registry, for either way out
  const leftRegistry = () => ({
    query: flow.query,
    resultsCount: search.results.length,
    normalizedQuery: search.normalizedQuery,
    dotLookup: search.dotLookup,
    searchedBeforeManual: search.searched && !search.searching,
  })

  /** A patch that sets only the two portable certifications is a copy. */
  const isCopiedAnswers = (patch: Record<string, unknown>): boolean => {
    const keys = Object.keys(patch)
    return (
      keys.length > 0 &&
      keys.every((key) =>
        (COPYABLE_CERTIFICATIONS as readonly string[]).includes(key)
      )
    )
  }

  /**
   * Every gesture goes through here so the events sit next to the transition
   * that caused them, rather than being scattered across the components.
   */
  const track = (event: Parameters<typeof dispatch>[0]) => {
    switch (event.type) {
      // Not openPeriod: that opens the employed/not-employed question, and an
      // unemployment answer never reaches the registry at all. Counting it as a
      // picker open inflated the search funnel with flows that never searched.
      case 'answerEmployed':
        if (event.employed) telemetry.pickerOpened('gap')
        break
      case 'openCurrentlyEmployed':
        telemetry.pickerOpened('empty_page')
        break
      // Only the search reopens the picker; editing recorded fields does not
      case 'searchEmployer':
        telemetry.pickerOpened('change_employer')
        break
      case 'selectCarrier':
        telemetry.carrierSelected({
          // 1-based position in the list the driver was looking at, so the
          // ranking readout means something
          rank:
            search.results.findIndex(
              (carrier) =>
                carrier.dotNumber === event.carrier.dotNumber &&
                carrier.name === event.carrier.name
            ) + 1,
          dotNumber: event.carrier.dotNumber,
        })
        break
      case 'enterManually':
        telemetry.manualEntry(leftRegistry())
        break
      case 'enterNonDriving':
        telemetry.nonDrivingJob(leftRegistry())
        break
      case 'switchToUnemployment':
        telemetry.gapNudgeAccepted({ query: flow.query })
        break
      // Once, when the step is finished — not on chooseContact, which the manual
      // fields dispatch on every keystroke. Typing a phone number emitted a
      // partial `source: manual` action per character, which would have made the
      // metric behind the 38.8% contact problem unreadable.
      //
      // Leaving it empty for a platform-only employer is not a skip: nothing was
      // needed. Reported as that instead, so the capture rate does not fall
      // every time a carrier is marked.
      case 'continueFromContact':
        if (flow.contact) {
          telemetry.contactAdded(flow.contact, { platformOnly })
        } else if (platformOnly) {
          telemetry.contactNotNeeded({ dotNumber: flow.draft.dotNumber })
        } else {
          telemetry.contactSkipped({ knownCount: knownContacts.length })
        }
        break
      case 'dismiss':
        if (flow.mode === 'search') telemetry.searchAbandoned()
        break
      case 'patch':
        // A copy arrives as a patch like any other edit, so it has to be
        // recognised here or the event never fires at all
        if (isCopiedAnswers(event.patch as Record<string, unknown>)) {
          telemetry.answersCopied()
        }
        break
      default:
        break
    }
    dispatch(event)
  }

  const problems = useMemo(() => schemaProblems(experiences), [experiences])

  const entries: ListEntry[] = useMemo(() => {
    const refused = new Set<FormExperience>(
      problems.map((problem) => problem.experience)
    )
    const list: ListEntry[] = []
    experiences.forEach((experience, index) => {
      const draft = toEntryDraft(experience)
      // A row holding nothing at all is a placeholder, not an entry.
      const blank =
        draft.startMonth == null &&
        draft.endMonth == null &&
        !draft.companyName &&
        !draft.unemploymentReason
      if (blank) return
      const attention = attentionFor(draft, nowMonth)
      list.push({
        id: experience.employmentExperienceId ?? `index-${index}`,
        kind: draft.kind,
        name: draft.companyName ?? undefined,
        startMonth: draft.startMonth ?? null,
        endMonth: draft.isCurrent ? null : draft.endMonth ?? null,
        ...(attention.attention === 'none' && refused.has(experience)
          ? { attention: 'invalid' as const, note: 'Validation errors' }
          : attention),
      })
    })
    return list
  }, [experiences, nowMonth, problems])

  // Offered as the source for "Same answers as ...": the most recent entry that
  // has answers to give.
  const copyFrom = useMemo(() => {
    const answered = experiences
      .map(toEntryDraft)
      .filter(
        (draft) =>
          draft.kind === 'employment' &&
          (draft.isDriverSubjectToFMCSRs != null ||
            draft.isJobDesignatedAsSafetySensitiveFunctionUnderDOT != null)
      )
      .sort((a, b) => (b.startMonth ?? 0) - (a.startMonth ?? 0))
    return answered[0] ?? null
  }, [experiences])

  const openEntry = (id: string) => {
    const experience = experiences.find(
      (candidate) => candidate.employmentExperienceId === id
    )
    if (!experience) return
    dispatch({ type: 'openEntry', entry: toEntryDraft(experience), id })
  }

  const handleRemove = () => {
    const id = flow.editingId
    if (id == null) return
    telemetry.entryRemoved({ kind: flow.draft.kind })
    removeEntry(id)
    dispatch({ type: 'dismiss' })
  }

  return (
    <div className="relative flex min-h-0 w-full flex-1 flex-col">
      <EmploymentHistoryList
        entries={entries}
        requiredYears={requiredYears}
        now={now}
        busy={busy || saving}
        onOpenEntry={openEntry}
        onOpenPeriod={(period: MonthPeriod) =>
          track({ type: 'openPeriod', period })
        }
        onCurrentlyEmployed={() => track({ type: 'openCurrentlyEmployed' })}
        onNotCurrentlyEmployed={() =>
          track({ type: 'openNotCurrentlyEmployed' })
        }
        onBack={onBack}
        backText={backText}
        onContinue={() => {
          // Emitted here rather than in the list, which does not know whether
          // the company enforces coverage
          const coverage = analyzeCoverage(experiences, requiredYears, now)
          telemetry.continuedWithGaps({
            gapCount: coverage.gaps.length,
            uncoveredMonths: coverage.windowMonths - coverage.coveredMonths,
            enforced: Boolean(requiredYears && requiredYears > 0),
          })
          // Named here, because the generic toast Continue raises asks the
          // driver to check highlighted fields this screen does not have
          if (problems.length > 0) {
            toast(describeSchemaProblem(problems[0]), 'error')
            return
          }
          onContinue()
        }}
      />
      <EntryModal
        state={flow}
        onEvent={track}
        knownContacts={knownContacts}
        contactsLoading={contactsLoading}
        contactNotNeeded={platformOnly}
        results={search.results}
        searching={search.searching}
        searchFailed={search.failed}
        searched={search.searched}
        onRemove={handleRemove}
        copyFrom={copyFrom}
        maxMonth={nowMonth}
      />
    </div>
  )
}
